import type { FeatureLauncherRoute } from '../models/control-form';
import { expandLauncherProperties, matchLauncherRoute, ResolvedLauncherRoute } from './feature-launcher';
import { resolveFeatureSourcePrefill } from './feature-source-prefill';

/**
 * Done vs pending per launcher point (Addendum 3). The api's feature-link summary lists, per feature, the records
 * linked to it (newest first); a chooser row is DONE when a form entry of the row's target form type links the
 * feature. Pure, so it runs under karma and matches mobile. Result values (Pass/Fail) are not interpreted.
 */

/** One linked record of `GET …/map-layers/:layerId/feature-link-summary`. */
export interface FeatureLinkEntry {
  entityType: string;
  entityId?: number | string | null;
  entityUuid?: string | null;
  controlFormId?: number | string | null;
  controlFormType?: string | null;
  controlFormName?: string | null;
  dailyReportId?: number | string | null;
  dailyReportUuid?: string | null;
  /** YYYY-MM-DD. */
  reportDate?: string | null;
  siteId?: string | null;
  createdBy?: { id?: string | null; name?: string | null } | null;
  createdAt?: string | null;
  /** report_form_header only: the entry's header values by field name (older api builds omit it). */
  fieldValues?: Record<string, unknown> | null;
}

export interface FeatureLinkSummary {
  layerId: string;
  features: Record<string, FeatureLinkEntry[]>;
}

export type FeaturePointStatus = 'done' | 'partial' | 'pending';

export const FORM_ENTRY_TYPE = 'report_form_header';

/**
 * The newest form entry of `formType` linked to the feature that also holds the row's `expected` header values (one
 * form for every test: the Density row needs test_type "Density Test"), or null (pending). An entry without
 * `fieldValues` (older api) counts on its form type alone.
 */
export function rowDoneEntry(entries: FeatureLinkEntry[] | null | undefined, formType: string | null | undefined,
  expected?: Record<string, unknown> | null): FeatureLinkEntry | null {
  if (!formType || !Array.isArray(entries)) return null;
  const matching = entries.filter(entry => entry?.entityType === FORM_ENTRY_TYPE && entry.controlFormType === formType
    && holdsValues(entry, expected));
  if (!matching.length) return null;
  // The api orders newest first; sort anyway (stable for equal or missing times).
  return matching.reduce((newest, entry) => (entryTime(entry) > entryTime(newest) ? entry : newest));
}

interface LauncherForStatus { routes: ResolvedLauncherRoute[]; multi_value_fields?: Record<string, string> }

/**
 * The header values a row's entry must hold: what the target form's feature_source prefill produces from the row's
 * properties, for the mappings fed by a routing (route `when`) or multi-value property. {} when the target has no
 * such mapping (the form type alone decides).
 */
export function launcherRowExpectations(route: ResolvedLauncherRoute | null | undefined,
  rowProperties: Record<string, unknown> | null | undefined, launcher: LauncherForStatus | null | undefined): Record<string, unknown> {
  const source = route?.form?.ui_config?.feature_source;
  if (!source?.fields || typeof source.fields !== 'object') return {};
  const routing = new Set([
    ...(launcher?.routes || []).flatMap(item => Object.keys(item?.when || {})),
    ...Object.keys(launcher?.multi_value_fields || {}),
    ...Object.keys(source.multi_value_fields || {}),
  ]);
  const fields = Object.fromEntries(Object.entries(source.fields).filter(([, mapping]) =>
    routing.has(typeof mapping === 'string' ? mapping : mapping?.field)));
  if (!Object.keys(fields).length) return {};
  return resolveFeatureSourcePrefill({ ...source, fields }, rowProperties, route.form.ui_config?.header?.fields || []);
}

/** A chooser row's entry: the newest one of its target form holding the row's expected values. */
export function launcherRowEntry(route: ResolvedLauncherRoute | null | undefined, rowProperties: Record<string, unknown> | null | undefined,
  launcher: LauncherForStatus | null | undefined, entries: FeatureLinkEntry[] | null | undefined): FeatureLinkEntry | null {
  if (!route?.form?.type) return null;
  return rowDoneEntry(entries, route.form.type, launcherRowExpectations(route, rowProperties, launcher));
}

/**
 * The point's status from its expected tests: one per chooser row (the routes its expanded properties match).
 * All done → done, some → partial, none → pending; null when it expects no test (no route matches).
 */
export function launcherPointStatus(properties: Record<string, unknown> | null | undefined,
  launcher: LauncherForStatus | null | undefined, entries: FeatureLinkEntry[] | null | undefined): FeaturePointStatus | null {
  if (!launcher) return null;
  const routing = { routes: launcher.routes as { when?: FeatureLauncherRoute['when'] }[], multi_value_fields: launcher.multi_value_fields };
  const rows = expandLauncherProperties(properties, routing)
    .map(row => ({ row, route: matchLauncherRoute(launcher.routes, row.properties) }))
    .filter(item => !!item.route);
  if (!rows.length) return null;
  const done = rows.filter(item => !!launcherRowEntry(item.route, item.row.properties, launcher, entries)).length;
  return done === rows.length ? 'done' : done ? 'partial' : 'pending';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 7" from YYYY-MM-DD (no time zone shift); '' when unreadable. */
export function shortReportDate(date: string | null | undefined): string {
  const match = typeof date === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!match) return '';
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? `${MONTHS[month - 1]} ${Number(match[3])}` : '';
}

/** A done row's subtitle: "<Form name> · <Mon D>, <name>" (parts that are missing are left out). */
export function doneCaption(entry: FeatureLinkEntry | null | undefined): string {
  if (!entry) return '';
  const when = [shortReportDate(entry.reportDate), entry.createdBy?.name?.trim()].filter(Boolean).join(', ');
  return [entry.controlFormName?.trim(), when].filter(Boolean).join(' · ');
}

/** True when the entry belongs to the given (currently open) daily report. */
export function entryInReport(entry: FeatureLinkEntry | null | undefined,
  report: { id?: unknown; uuid?: unknown } | null | undefined): boolean {
  if (!entry || !report) return false;
  const ids = [report.id, report.uuid].filter(value => value !== null && value !== undefined && value !== '').map(String);
  return [entry.dailyReportId, entry.dailyReportUuid].some(value => value !== null && value !== undefined && ids.includes(String(value)));
}

/** Every expected value equals the entry's stored one (trimmed, case-insensitive; arrays as sets). */
function holdsValues(entry: FeatureLinkEntry, expected: Record<string, unknown> | null | undefined): boolean {
  const keys = Object.keys(expected || {});
  if (!keys.length) return true;
  const stored = entry.fieldValues;
  if (!stored || typeof stored !== 'object') return true;
  return keys.every(key => sameValue(stored[key], expected[key]));
}

function valueSet(value: unknown): string[] {
  let list: unknown = value;
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try { list = JSON.parse(value); } catch { list = value; }
  }
  const items = Array.isArray(list) ? list : [list];
  return items.filter(item => item !== null && item !== undefined && String(item).trim() !== '')
    .map(item => String(item).trim().toLowerCase()).sort();
}

function sameValue(stored: unknown, expected: unknown): boolean {
  const left = valueSet(stored);
  const right = valueSet(expected);
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

/**
 * The report's own record of a summary entry (its form entries list), matched by the entry's id or uuid against the
 * record's id or uuid (the web's report data carries the uuid as `id`). Null when the report does not list it.
 */
export function findLinkedRecord<T extends { id?: unknown; uuid?: unknown }>(records: T[] | null | undefined,
  entry: FeatureLinkEntry | null | undefined): T | null {
  if (!entry) return null;
  const ids = [entry.entityId, entry.entityUuid].filter(value => value !== null && value !== undefined && value !== '').map(String);
  if (!ids.length) return null;
  return (records || []).find(record => [record?.id, record?.uuid]
    .some(value => value !== null && value !== undefined && ids.includes(String(value)))) || null;
}

function entryTime(entry: FeatureLinkEntry): number {
  const time = Date.parse(entry?.createdAt || '');
  return Number.isFinite(time) ? time : -Infinity;
}
